import { Box, styled, Tab, TabProps, Tabs, TabsProps, useTheme } from "@mui/material";
import React from "react";

export interface VrsTabsContainerProps extends TabsProps {
  tabsContainerContent: { [label: string]: JSX.Element };
  children?: React.ReactNode;
  withTopMargin?: boolean;
  tabValue?: number;
}

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vrs-tabpanel-${index}`}
      aria-labelledby={`vrs-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
};

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop: string) => prop !== "clicked",
})<TabProps & { clicked: boolean }>(({ theme, clicked }) => ({
  ...theme.typography.subtitle2,
  padding: theme.spacing(1.75, 1.25),
  minWidth: "130px",
  whiteSpace: "nowrap",
  textAlign: "center",
  color: theme.veris.colors.neutrals["grey-3"],
  textTransform: "capitalize",
  backgroundColor: clicked
    ? theme.veris.colors.neutrals.white
    : theme.veris.colors.neutrals["soft-white"],
  borderRight: `1px solid ${theme.veris.colors.neutrals["grey-light"]}`,
  "&:focus, &:active": {
    color: theme.veris.colors.amethyst.normal,
    backgroundColor: theme.veris.colors.neutrals.white,
  },
  "&.Mui-selected": {
    ...theme.typography.subtitle1,
  },
}));

export function VrsTabsContainer({
  tabsContainerContent,
  children,
  withTopMargin,
  tabValue,
  sx,
  onChange,
}: VrsTabsContainerProps): JSX.Element {
  const theme = useTheme();
  const [value, setValue] = React.useState(tabValue || 0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (typeof onChange === "function") {
      const v = Object.keys(tabsContainerContent)[newValue];
      onChange(event, v);
    }
    setValue(newValue);
  };

  const tabProps = (index: number) => {
    return {
      id: `vrs-tab-${index}`,
      "aria-controls": `vrs-tabpanel-${index}`,
    };
  };

  return (
    <Box
      key={value}
      mb={1}
      sx={{
        width: "100%",
        borderRadius: +theme.shape.borderRadius * 0.375,
        border: `1px solid ${theme.veris.colors.neutrals["grey-light"]}`,
        background: theme.veris.colors.neutrals.white,
        marginTop: withTopMargin ? theme.spacing(1) : 0,
      }}
      data-test-hook="vrsTabsContainer-container-element"
    >
      <Tabs
        key={value}
        value={value}
        onChange={handleChange}
        aria-label="vrs tabs"
        TabIndicatorProps={{ style: { backgroundColor: theme.veris.colors.neutrals.white } }}
        sx={{
          width: "100%",
          borderBottom: `1px solid ${theme.veris.colors.neutrals["grey-light"]}`,
          ...sx,
        }}
      >
        {Object.keys(tabsContainerContent).map((label, index) => (
          <StyledTab label={label} {...tabProps(index)} key={label} clicked={value === index} />
        ))}
        {children}
      </Tabs>
      {Object.values(tabsContainerContent).map((content, index) => (
        <TabPanel value={value} index={index} key={content.key}>
          {content}
        </TabPanel>
      ))}
    </Box>
  );
}
